import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusCircleOutlined,
  RiseOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Divider } from 'antd';
import { flatten, isEqual, isFinite, round, sortBy, startCase } from 'lodash';
import { type ComponentType, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { getOrderedAncestors } from 'components/data/assets/assetColumnRenderers';
import { DamageNumberText, None } from 'components/data/helpers';
import { getUniqueIdentifier } from 'horizon/components/Assets/AssetDetails/helpers';
import type {
  BladeTarget,
  PictureTarget,
  TowerOrTransitionPieceTarget,
} from 'horizon/routes/Inspections/types';
import { getInspectionVendorDisplayName } from 'horizon/routes/Inspections/vendorUtils';
import {
  type AtlasGqlAsset,
  type AtlasGqlHorizonAnnotation,
  type AtlasGqlHorizonDamage,
  type AtlasGqlHorizonDamageObservation,
  type AtlasGqlHorizonDamageObservationGroup,
  type AtlasGqlHorizonDamagePropagation,
  type AtlasGqlInspection,
  type AtlasGqlTargetTower_External,
  type AtlasGqlTargetTransition_Piece,
  type AtlasGqlTask,
  type AtlasGqlTaskTarget,
  AtlasGqlPropagationType,
  AtlasGqlTaskTargetType,
} from 'types/atlas-graphql';
import { type HorizonTheme } from 'utils/color/horizonTheme';
import { formatShortId } from 'utils/format';
import {
  type OrderableInspection,
  byInspectionRecency,
  compareIds,
  findPreviousDayItem,
  sortByInspectionRecency,
  sortByInspectionRecencyDesc,
} from 'utils/inspectionOrder';
import { ModalLink } from 'utils/router';
import type { RecursivePartial } from 'utils/types';

import type { PreviousDamageInfo, PreviousInspectionInfo } from './HorizonDamageImage/types';
import { getPreviousInspectionDetails } from './HorizonDamageImage/utils';

export const PROPAGATION_ICON_MAP: Record<
  AtlasGqlPropagationType,
  { icon: ComponentType<{ style?: CSSProperties }>; color: keyof HorizonTheme }
> = {
  [AtlasGqlPropagationType.PropagationLight]: { icon: RiseOutlined, color: 'green' },
  [AtlasGqlPropagationType.PropagationMedium]: { icon: RiseOutlined, color: 'orange' },
  [AtlasGqlPropagationType.PropagationHeavy]: { icon: RiseOutlined, color: 'red' },
  [AtlasGqlPropagationType.NoChange]: { icon: CheckCircleOutlined, color: 'teal' },
  [AtlasGqlPropagationType.Formed]: { icon: PlusCircleOutlined, color: 'teal' },
  [AtlasGqlPropagationType.RepairFailed]: { icon: CloseCircleOutlined, color: 'red' },
  [AtlasGqlPropagationType.RepairIncomplete]: { icon: WarningOutlined, color: 'orange' },
  [AtlasGqlPropagationType.ProgressionHeavy]: { icon: RiseOutlined, color: 'red' },
  [AtlasGqlPropagationType.ProgressionMedium]: { icon: RiseOutlined, color: 'orange' },
  [AtlasGqlPropagationType.ProgressionLight]: { icon: RiseOutlined, color: 'green' },
  [AtlasGqlPropagationType.ProgressionLegacy]: { icon: RiseOutlined, color: 'orange' },
};

export const getObservationType = (
  observation: AtlasGqlHorizonDamageObservation | null | undefined,
  hasVendorNames: boolean
): string => {
  const inspection = observation?.inspection;
  const vendorName = getInspectionVendorDisplayName(inspection, hasVendorNames) ?? '';
  const typeName = inspection?.type?.name ?? '';

  // Either alone still tells two same-day Inspections apart.
  return [vendorName, typeName].filter(Boolean).join(' — ');
};

export const _propagationTypeMapping = Object.fromEntries(
  Object.values(AtlasGqlPropagationType).map((t) => [t, startCase(t)])
);

// Mapping of Gql Propagation Type enum to render-friendly values
export const propagationTypeMapping = {
  // TODO: remove 'Progression' .filter()
  ...Object.fromEntries(
    Object.values(AtlasGqlPropagationType)
      .filter((type) => !type.includes('Progression'))
      .filter((type) => !type.includes('Propagation'))
      .map((t) => [t, startCase(t)])
  ),
  // Sort propagation values in order of severity
  ...Object.fromEntries(
    [
      AtlasGqlPropagationType.PropagationLight,
      AtlasGqlPropagationType.PropagationMedium,
      AtlasGqlPropagationType.PropagationHeavy,
    ].map((t) => [t, startCase(t).split(' ').join(' - ')])
  ),
};

export const truncatePropagationNotes = (notes: string, maxLength: number = 256) =>
  notes.length > maxLength ? `${notes.substring(0, maxLength + 1)}...` : notes;

export const getObservationsFromDamage = (damage: AtlasGqlHorizonDamage | null | undefined) => {
  return (damage?.observationGroups ?? []).reduce((acc, value) => {
    const observationsInGroup = (value?.observations ?? []).filter(
      (o): o is AtlasGqlHorizonDamageObservation => !!o
    );
    return [...acc, ...observationsInGroup];
  }, [] as AtlasGqlHorizonDamageObservation[]);
};

export const getObservationsFromObservationGroups = (
  observationGroups: (AtlasGqlHorizonDamageObservationGroup | null | undefined)[]
) => flatten(observationGroups.map((group) => group?.observations ?? []));

/**
 * The `groupAttrs` fallback mirrors `inspectionDateOf` in the damages service, so a group whose
 * Inspection was not selected still orders by date rather than sorting as undated.
 */
export const getObservationGroupInspection = (
  observationGroup: AtlasGqlHorizonDamageObservationGroup | null | undefined
): OrderableInspection | null | undefined => {
  const inspection = observationGroup?.observations?.[0]?.inspection;
  const groupAttrs = observationGroup?.groupAttrs;

  return {
    id: inspection?.id ?? observationGroup?.inspectionId,
    inspectionDate: inspection?.inspectionDate ?? groupAttrs?.Date ?? groupAttrs?.date,
    created: inspection?.created,
  };
};

export const orderObservationGroupsDesc = <
  T extends AtlasGqlHorizonDamageObservationGroup | null | undefined,
>(
  observationGroups: T[]
): T[] =>
  sortByInspectionRecencyDesc(observationGroups, (group) => getObservationGroupInspection(group));

export const getPreviousObservationGroup = <
  T extends AtlasGqlHorizonDamageObservationGroup | null | undefined,
>(
  observationGroups: T[],
  observationGroup: T
): T | undefined =>
  findPreviousDayItem(observationGroups, observationGroup, (group) =>
    getObservationGroupInspection(group)
  );

/**
 * The service guards Propagation uniqueness on the (source, target) pair, not on the target alone,
 * so a group can be the target of more than one live Propagation. Propagations recorded before
 * same-day Inspections were ordered can have a sibling as their source, so a non-previous source
 * is still surfaced rather than hidden.
 */
export const resolveGroupPropagation = (
  propagations: (AtlasGqlHorizonDamagePropagation | null | undefined)[],
  observationGroups: (AtlasGqlHorizonDamageObservationGroup | null | undefined)[],
  observationGroup: AtlasGqlHorizonDamageObservationGroup | null | undefined
): AtlasGqlHorizonDamagePropagation | null => {
  if (!observationGroup?.id) {
    return null;
  }

  const targeting = propagations.filter(
    (p): p is AtlasGqlHorizonDamagePropagation =>
      !!p && p.targetObservationGroup?.id === observationGroup.id
  );
  const previousGroup = getPreviousObservationGroup(observationGroups, observationGroup);
  const sourceInspectionOf = ({ sourceObservationGroup }: AtlasGqlHorizonDamagePropagation) =>
    getObservationGroupInspection(
      observationGroups.find((g) => !!g?.id && g.id === sourceObservationGroup?.id)
    );

  return (
    targeting.find((p) => p.sourceObservationGroup?.id === previousGroup?.id) ??
    [...targeting].sort(
      (left, right) =>
        byInspectionRecency(sourceInspectionOf(right), sourceInspectionOf(left)) ||
        compareIds(left.id, right.id)
    )[0] ??
    null
  );
};

/**
 * Damages carrying two flagged primaries exist in production unrepaired, so the flagged groups
 * are ranked rather than taken by array position.
 */
export const resolvePrimaryObservationGroup = (
  observationGroups: (AtlasGqlHorizonDamageObservationGroup | null | undefined)[]
): AtlasGqlHorizonDamageObservationGroup | null => {
  const flagged = observationGroups.filter((og) => og?.isPrimaryObservationGroup);

  return orderObservationGroupsDesc(flagged.length ? flagged : observationGroups)?.[0] ?? null;
};

export const getPrimaryObservationGroupForDamage = (
  damage: AtlasGqlHorizonDamage | null | undefined
): AtlasGqlHorizonDamageObservationGroup | null =>
  resolvePrimaryObservationGroup(damage?.observationGroups ?? []);

export const getPrimaryObservationForDamage = (
  damage: AtlasGqlHorizonDamage | null | undefined
): AtlasGqlHorizonDamageObservation | null => {
  // get the observation group marked as primary or fall back to the most recent group
  const observationGroup = getPrimaryObservationGroupForDamage(damage);

  // return the primary observation within that group
  return observationGroup ? getPrimaryObservationForObservationGroup(observationGroup) : null;
};

export const getPrimaryObservationForObservationGroup = (
  observationGroup: AtlasGqlHorizonDamageObservationGroup | null | undefined
): AtlasGqlHorizonDamageObservation | null => {
  // get the observation by source observation id or fall back to the first observation
  const observation =
    (observationGroup?.observations ?? []).find(
      (o) => o?.id === observationGroup?.sourceObservationId
    ) ?? observationGroup?.observations?.[0];

  // return the primary observation
  return observation ?? null;
};

export const getObservationGroupByInspectionId = (
  damage: AtlasGqlHorizonDamage | null | undefined,
  inspectionId: string
): AtlasGqlHorizonDamageObservationGroup | null | undefined =>
  damage?.observationGroups?.find((g) => g?.inspectionId === inspectionId);

// Given an array of task targets, returns the inspection id
// of the inspection task target (or undefined)
export const getTaskTargetInspectionId = (
  targets: RecursivePartial<AtlasGqlTaskTarget>[]
): string | undefined => {
  const inspectionTarget: Partial<AtlasGqlInspection> | undefined = targets?.find(
    ({ targetType }) => targetType === AtlasGqlTaskTargetType.Inspection
  )?.target as AtlasGqlInspection;

  return inspectionTarget?.id;
};

/**
 * In some cases, we want to render the attributes of the task target's observation
 * group (i.e. the primary group at the time of task creation). This function finds that
 * group and sets its attributes as `primaryObservationGroupAttrs` for rendering convenience.
 * This should only be called when task locking is enabled.
 */
export const mapTargetObservationGroupAttrsToPrimary = (task: AtlasGqlTask) => {
  if (
    'targets' in task &&
    task.targets?.length &&
    'horizonDamage' in task &&
    !!task.horizonDamage
  ) {
    const targetInspectionId = getTaskTargetInspectionId(task.targets);
    const damage = task.horizonDamage as AtlasGqlHorizonDamage;
    const targetObservationGroupAttrs =
      (targetInspectionId &&
        getObservationGroupByInspectionId(damage, targetInspectionId)?.groupAttrs) ||
      damage.primaryObservationGroupAttrs;

    return {
      ...task,
      horizonDamage: {
        ...task.horizonDamage,
        primaryObservationGroupAttrs: targetObservationGroupAttrs,
      },
    };
  }

  return task;
};

// Get all possible observations for linking from inspections, filtered to be valid
// (same assetId/schemaId, but different damageId than the primaryObservation)
export const getObservationsForLinking = (
  damage: AtlasGqlHorizonDamage | null | undefined,
  inspections: AtlasGqlInspection[]
): AtlasGqlHorizonDamageObservation[] => {
  const primaryObservation = getPrimaryObservationForDamage(damage);
  return flatten(
    inspections.map(({ horizonDamageObservations }) => horizonDamageObservations)
  ).filter(
    (observation) =>
      observation &&
      observation.assetId === primaryObservation?.assetId &&
      observation.schema?.id === primaryObservation?.schema?.id &&
      observation.damageId !== primaryObservation?.damageId
  ) as AtlasGqlHorizonDamageObservation[];
};

export const getPictureTargetDistance = (target: PictureTarget | undefined): number | undefined => {
  const distance = Number(
    (target as BladeTarget)?.radialDistance ?? (target as TowerOrTransitionPieceTarget)?.height
  );
  return isFinite(distance) ? distance : undefined;
};

// TODO: add schema attributes for other asset types besides blades
export const getAttrsDistance = (attrs: { [key: string]: any } | undefined): number | undefined => {
  const distance = Number(attrs?.['Distance']);
  return isFinite(distance) ? distance : undefined;
};

export const getPictureTargetSide = (target: PictureTarget | undefined): string | undefined => {
  const side =
    (target as BladeTarget)?.bladeSide ??
    (target as AtlasGqlTargetTower_External)?.towerSide ??
    (target as AtlasGqlTargetTransition_Piece)?.transitionPieceSide;
  return side ? startCase(side) : undefined;
};

// TODO: add schema attributes for other asset types besides blades
export const getAttrsSide = (attrs: { [key: string]: any } | undefined): string | undefined => {
  const side = attrs?.['Blade Side'];
  return side ? startCase(side) : undefined;
};

export const sortSuggestions = (
  damage: AtlasGqlHorizonDamage,
  suggestions: AtlasGqlHorizonDamageObservation[]
) => {
  const damageAttrs = damage.primaryObservationGroupAttrs;
  const primaryObservation = getPrimaryObservationForDamage(damage);

  const damageDistance =
    getAttrsDistance(damageAttrs) ??
    getPictureTargetDistance(primaryObservation?.picture?.target) ??
    0;
  const damageSide =
    getAttrsSide(damageAttrs) ?? getPictureTargetSide(primaryObservation?.picture?.target) ?? '';

  // sort observations by
  // - closest to given distance (blade) or height (tower/transition piece)
  // - closest to given side (blade/tower/transition piece)
  return sortBy(suggestions, [
    (observation) => {
      const observationDistance =
        getAttrsDistance(observation.attrs) ??
        getPictureTargetDistance(observation.picture?.target) ??
        0;
      return Math.abs(damageDistance - observationDistance);
    },
    (observation) => {
      const observationSide =
        getAttrsSide(observation.attrs) ?? getPictureTargetSide(observation.picture?.target) ?? '';
      return damageSide === observationSide ? 0 : observationSide;
    },
  ]);
};

const _getOldestOrNewestObservationForDamage = (
  damage: AtlasGqlHorizonDamage,
  position: 'oldest' | 'newest'
): AtlasGqlHorizonDamageObservation | null => {
  // sort observation groups ascending or descending to find the oldest or newest group
  const orderGroups = position === 'oldest' ? sortByInspectionRecency : sortByInspectionRecencyDesc;
  const observationGroup = orderGroups(
    damage?.observationGroups ?? [],
    getObservationGroupInspection
  )?.[0];

  // return the primary observation within that group
  return observationGroup ? getPrimaryObservationForObservationGroup(observationGroup) : null;
};

export const getOldestObservationForDamage = (
  damage: AtlasGqlHorizonDamage
): AtlasGqlHorizonDamageObservation | null =>
  _getOldestOrNewestObservationForDamage(damage, 'oldest');

export const getNewestObservationForDamage = (
  damage: AtlasGqlHorizonDamage
): AtlasGqlHorizonDamageObservation | null =>
  _getOldestOrNewestObservationForDamage(damage, 'newest');

export const renderAttribute = (
  attrs: { [key: string]: any },
  key: string,
  unit: string = ''
): string | JSX.Element => {
  return attrs?.[key] ? `${formatAttribute(attrs[key])}${unit}` : <None />;
};

/**
 * Hack for getting legacy damage id from the picture data
 * by matching coordinates. This is intended for temporary
 * use for constructing links while both damage systems
 * are still in place.
 */
export const getLegacyDamageIdForObservation = (
  observation?: AtlasGqlHorizonDamageObservation | null
): string | undefined =>
  observation?.picture?.damages?.find(
    ({ observations }) =>
      (observations as AtlasGqlHorizonAnnotation[]).findIndex(({ coordinates }) =>
        isEqual(coordinates, observation?.coordinates)
      ) !== -1
  )?.id;

export const formatDamageId = ({ id }: Partial<AtlasGqlHorizonDamage>): string =>
  id ? formatShortId(id) : '';

export const formatObservationId = ({ id }: Partial<AtlasGqlHorizonDamageObservation>): string =>
  id ? formatShortId(id) : '';

export const formatAttribute = (value: number | string): number | string =>
  typeof value === 'number' ? round(value, 2) : value;

export const DamageNumber = ({ damageId }: { damageId: string }) => (
  <DamageNumberText>{formatDamageId({ id: damageId })}</DamageNumberText>
);

export const DamageLink = ({ damageId, target }: { damageId: string; target?: string }) => (
  <Link to={`/damages2/${damageId}`} target={target} className="testid-damage-link">
    <DamageNumberText>{formatDamageId({ id: damageId })}</DamageNumberText>
  </Link>
);

export const InspectionLink = ({
  id,
  label = formatShortId(id),
}: {
  id: string;
  label?: string;
}) => <Link to={`/inspections/${id}`}>{label}</Link>;

export const ObservationLink = ({
  observationId,
  pictureId,
  target,
}: {
  observationId: string;
  pictureId: string | undefined;
  target?: string;
}) =>
  pictureId ? (
    <ModalLink to={`/pictures/${pictureId}?observation=${observationId}&version=2`} target={target}>
      <DamageNumberText>{formatObservationId({ id: observationId })}</DamageNumberText>
    </ModalLink>
  ) : (
    <DamageNumberText>{formatObservationId({ id: observationId })}</DamageNumberText>
  );

export const AssetLink = ({ asset, target }: { asset: AtlasGqlAsset; target?: string }) => {
  return (
    <>
      {getOrderedAncestors(asset).map((a) => (
        <span key={a?.id}>
          <Link to={`/assets/${a?.id}`} target={target}>
            {asset && getUniqueIdentifier(a)}
          </Link>
          <Divider type="vertical" />
        </span>
      ))}
      <Link to={`/assets/${asset?.id}`} target={target}>
        {asset && (asset?.uniqueIdLabel ?? getUniqueIdentifier(asset))}
      </Link>
    </>
  );
};

export const resolveCompareData = ({
  isExternalInspection,
  previousDamageInfo,
  sourceObservation,
}: {
  isExternalInspection: boolean;
  previousDamageInfo?: PreviousDamageInfo;
  sourceObservation?: AtlasGqlHorizonDamageObservation | null;
}):
  | { previousDamageInfo?: PreviousDamageInfo; prevInspectionDetails?: PreviousInspectionInfo }
  | undefined => {
  if (!isExternalInspection) {
    return;
  }

  if (previousDamageInfo?.previousPictureId && previousDamageInfo?.previousObservationId) {
    return { previousDamageInfo };
  }
  if (sourceObservation) {
    const prevInspection = getPreviousInspectionDetails(sourceObservation);
    if (prevInspection) {
      return { prevInspectionDetails: prevInspection };
    }
  }
  return {};
};
